import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import InvoicePdfView from "../components/InvoicePdfView";
import { API_BASE } from "../utils/api";

export default function InvoiceStatusModal({ embedded, onBack }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const loggedDealer = JSON.parse(localStorage.getItem("dealer") || "{}");

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!loggedDealer?.phone) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          `${API_BASE}/api/dealer-invoices?phone=${loggedDealer.phone}`
        );
        const data = await res.json();

        if (data.success) {
          setInvoices(data.invoices);
        }
      } catch (err) {
        console.error("Failed to fetch invoices:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const handleViewInvoice = async (summaryInv) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/invoice/${summaryInv.id}` // id is invoiceNumber
      );
      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Failed to load invoice");
        return;
      }
      setSelectedInvoice(data.invoice);
      setShowPreview(true);
    } catch (err) {
      console.error("Failed to load invoice:", err);
      alert("Failed to load invoice details");
    }
  };

  const handleDownload = async (invoice, index) => {
    if (!invoice.pdfUrl) {
      alert("PDF not available yet");
      return;
    }

    setDownloading((prev) => ({ ...prev, [index]: true }));

    try {
      const response = await fetch(invoice.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed");
    } finally {
      setTimeout(() => {
        setDownloading((prev) => ({ ...prev, [index]: false }));
      }, 1000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "text-green-600 bg-green-100";
      case "unpaid":
        return "text-red-600 bg-red-100";
      default:
        return "text-orange-600 bg-orange-100"; // e.g. pending/partial
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10">
      {/* header same as before */}

      {invoices.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-xl text-gray-500">No invoices found yet</p>
          <p className="text-sm text-gray-400 mt-2">
            Your invoices will appear here once generated
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {invoices.map((inv, i) => (
            <motion.div
              key={inv.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.08 }}
              className="bg-gradient-to-br from-yellow-50 to-white rounded-2xl p-6 border-2 border-yellow-200 hover:shadow-2xl transition-all hover:scale-105"
            >
              {/* 🔹 Top row: ID + date + status tag */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">
                    {inv.id}
                  </h3>
                  <p className="text-gray-600">{inv.date}</p>
                </div>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                    inv.status
                  )}`}
                >
                  {inv.status === "paid"
                    ? "Paid"
                    : inv.status === "unpaid"
                    ? "Unpaid"
                    : "Pending"}
                </span>
              </div>

              {/* Amount */}
              <p className="text-3xl font-bold text-gray-800 mb-6">
                {inv.amount}
              </p>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleViewInvoice(inv)}
                  className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 border border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                >
                  View Invoice
                </button>

                <button
                  onClick={() => handleDownload(inv, i)}
                  disabled={!inv.pdfUrl || downloading[i]}
                  className={`w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-3 transition-all ${
                    inv.pdfUrl
                      ? "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 shadow-lg"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {downloading[i] ? (
                    <>
                      <DownloadDoneIcon />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <DownloadIcon />
                      {inv.pdfUrl ? "Download PDF" : "PDF Not Ready"}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Preview modal with div -> PDF */}
      {showPreview && selectedInvoice && (
        <InvoicePdfView
          invoice={selectedInvoice}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
